<script setup lang="ts">
import { actingTenant, tenantOptions } from '@yc/api-client'
import type { TenantView } from '@yc/api-client'
import { computed, onMounted, ref } from 'vue'

import { useSession } from '../composables/useSession'

const emit = defineEmits<{
  (e: 'changed', tenantId: number): void
}>()

const { operator, actingTenantId } = useSession()

const ALL = 0

const options = ref<TenantView[]>([])
const loading = ref(false)
const failed = ref(false)

async function load(): Promise<void> {
  if (!operator.value) return
  loading.value = true
  failed.value = false
  try {
    options.value = await tenantOptions()
  } catch {
    failed.value = true
  } finally {
    loading.value = false
  }
}

onMounted(load)

const current = computed(() => actingTenantId.value ?? ALL)

async function onSelect(id: number): Promise<void> {
  if (id === current.value) return
  await actingTenant(id)
  emit('changed', id)
}
</script>

<template>
  <el-select
    v-if="operator"
    class="yc-tenant-switcher"
    :model-value="current"
    :loading="loading"
    size="small"
    placeholder="切换货主"
    @change="onSelect"
  >
    <el-option :value="ALL" label="全部货主" />
    <el-option v-for="t in options" :key="t.id" :value="t.id" :label="`${t.name}（${t.code}）`" />
  </el-select>
  <el-tooltip v-if="operator && failed" content="货主列表加载失败" placement="bottom">
    <el-button size="small" text @click="load">重试</el-button>
  </el-tooltip>
</template>

<style scoped lang="scss">
.yc-tenant-switcher {
  // 必须给确定的 width，不能只写 min-width：.el-select 自带 width:100%，
  // 只靠 min-width 时它的 flex 基准宽度就是父容器的 100%，会把顶栏剩余空间全吃掉
  // （实测 1024px 下涨到 516px），既挤没了昵称的省略号，也让选择器长得离谱。
  width: 220px;
  // 不参与压缩：顶栏变窄时被压扁的应该是昵称（可省略），
  // 而不是货主选择器（压扁后连当前货主名都读不全）。
  flex: none;
}
</style>